/**
 * Structural enums — the values here are INVARIANTS (see docs/CONVENTIONS.md →
 * Configuration vs invariants). Business option lists (agencies, frequencies,
 * designations, stages…) are NOT here: they live in reference_lists /
 * workflow_states and are edited by an administrator.
 */

// ---- User types are structural: which "world" a login lives in ----
export const USER_TYPES = ["internal", "company", "mashgiach", "location_user"] as const;
export type UserType = (typeof USER_TYPES)[number];

// ---- Modules mirror the departments; role_permissions grants a level per module ----
export const MODULES = [
  "companies",
  "onboarding",
  "locations",
  "ingredients",
  "products",
  "productions",
  "inspections",
  "events",
  "compliance",
  "certificates",
  "billing",
  "accounts_payable",
  "holograms",
  "shechita",
  "stam",
  "directory",
  "wine",
  "esrogim",
  "membership",
  "settings",
  "reports",
] as const;
export type Module = (typeof MODULES)[number];

export const MODULE_LABELS: Record<Module, string> = {
  companies: "Companies",
  onboarding: "Onboarding",
  locations: "Facilities & plants",
  ingredients: "Ingredients",
  products: "Products",
  productions: "Productions",
  inspections: "Inspections",
  events: "Events",
  compliance: "Compliance",
  certificates: "Certificates",
  billing: "Billing",
  accounts_payable: "Accounts payable",
  holograms: "Seals & holograms",
  shechita: "Shechita",
  settings: "Admin settings",
  reports: "Reports",
  stam: "STaM & practitioners",
  directory: "מפתח (consumer guide)",
  wine: "Wine programme",
  esrogim: "Esrogim",
  membership: "Membership",
};

export const PERMISSION_LEVELS = ["none", "read", "write", "approve", "admin"] as const;
export type PermissionLevel = (typeof PERMISSION_LEVELS)[number];

/** Shipped default role keys. Roles are admin-creatable; these are the seeded ones. */
export const DEFAULT_ROLE_KEYS = [
  "admin",
  "rc",
  "accounting",
  "ingredient_reviewer",
  "directory_team",
  "reporting",
  "company_user",
  "mashgiach",
  "facility_user",
] as const;
export type DefaultRoleKey = (typeof DEFAULT_ROLE_KEYS)[number];

// ---- Certification state of a company: fixed, drives hard gating ----
export const COMPANY_STATUSES = [
  "prospect",
  "onboarding",
  "certified",
  "suspended",
  "terminated",
  "follow_client_feedback",
] as const;
export type CompanyStatus = (typeof COMPANY_STATUSES)[number];

export const COMPANY_STATUS_LABELS: Record<CompanyStatus, string> = {
  prospect: "Prospect",
  onboarding: "Onboarding",
  certified: "Certified",
  suspended: "Suspended",
  terminated: "Terminated",
  follow_client_feedback: "Follow client feedback",
};

export const PRODUCTION_TYPES_OF_COMPANY = ["production", "food_service", "both"] as const;
export type CompanyProductionType = (typeof PRODUCTION_TYPES_OF_COMPANY)[number];

export const BILLING_MODES = ["own", "rollup"] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

export const OWNERSHIP_TYPES = ["OWN", "MFG"] as const;
export type OwnershipType = (typeof OWNERSHIP_TYPES)[number];

export const LOCATION_TYPES = ["facility", "plant"] as const;
export type LocationType = (typeof LOCATION_TYPES)[number];

// ---- Identity providers ----
export const IDENTITY_PROVIDERS = ["microsoft", "google"] as const;
export type IdentityProvider = (typeof IDENTITY_PROVIDERS)[number];

// ---- Person roles (a human on a record, independent of any login) ----
export const PERSON_ROLES = [
  "rc",
  /** the rabbinic authority who may approve ingredients and products (ingredients.md, products-formulas.md) */
  "ingredient_approver",
  "mashgiach",
  "shochet",
  "menaker",
  "sofer",
  "bodek",
  "socher",
  "tier",
  "supervisor",
  "stam_manager",
  "hologram_custodian",
  "facility_rep",
  "owner",
  "contact",
] as const;
export type PersonRole = (typeof PERSON_ROLES)[number];

// ---- Comments ----
export const COMMENT_VISIBILITY = ["internal", "shared"] as const;
export type CommentVisibility = (typeof COMMENT_VISIBILITY)[number];

/** Entities that never offer a shared comment. Invariant. */
export const NEVER_SHARED_COMMENT_ENTITIES = ["inspection", "issue", "complaint"] as const;

// ---- Field visibility ----
export const FIELD_RESTRICTIONS = ["assigned_rc"] as const;
export type FieldRestriction = (typeof FIELD_RESTRICTIONS)[number];

// ---- Custom fields ----
export const CUSTOM_FIELD_TYPES = ["text", "number", "date", "list", "bool", "document"] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];
export const CUSTOM_FIELD_SUBJECTS = ["company", "location", "product", "ingredient"] as const;
export type CustomFieldSubject = (typeof CUSTOM_FIELD_SUBJECTS)[number];

// ---- Access tokens (sign-in-free links) ----
export const ACCESS_TOKEN_PURPOSES = ["open_production", "submit_invoice", "complete_ingredients"] as const;
export type AccessTokenPurpose = (typeof ACCESS_TOKEN_PURPOSES)[number];

// ---- Ingredients / products (statuses that drive integrity are fixed) ----
export const COMPANY_INGREDIENT_STATUSES = [
  "pending",
  "under_review",
  "approved",
  "rejected",
  "expiring",
  "expired",
] as const;
export type CompanyIngredientStatus = (typeof COMPANY_INGREDIENT_STATUSES)[number];

export const MASTER_APPROVAL_STATUSES = ["approved", "rejected", "per_location"] as const;
export type MasterApprovalStatus = (typeof MASTER_APPROVAL_STATUSES)[number];

export const PASSOVER_STATUSES = ["approved", "not_approved", "not_assessed", "lapsed"] as const;
export type PassoverStatus = (typeof PASSOVER_STATUSES)[number];

// ---- Inspections ----
export const INSPECTION_TYPES = ["facility", "plant_production", "event"] as const;
export type InspectionType = (typeof INSPECTION_TYPES)[number];
export const INSPECTION_REVIEW_STATES = ["draft", "filed", "reviewed", "closed"] as const;
export type InspectionReviewState = (typeof INSPECTION_REVIEW_STATES)[number];

// ---- Form templates ----
export const FORM_FIELD_TYPES = [
  "text",
  "number",
  "date",
  "time",
  "dropdown",
  "checkbox",
  "yesno",
  "table",
  "textarea",
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];
export const FORM_FIELD_CADENCES = ["every_visit", "annual", "every_n_months"] as const;
export type FormFieldCadence = (typeof FORM_FIELD_CADENCES)[number];

// ---- Audit ----
export const AUDIT_ACTIONS = [
  "create",
  "update",
  "delete",
  "restore",
  "login",
  "logout",
  "approve",
  "reject",
  "transition",
  "access",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

// ---- Documents ----
export const DOCUMENT_OWNER_TYPES = [
  "company",
  "facility",
  "plant",
  "master_ingredient",
  "company_ingredient",
  "product",
  "production",
  "inspection",
  "loc",
  "invoice",
  "issue",
  "comment",
  "person",
  "ingredient_submission",
  "listing",
  "location_authorization",
  "stam_submission",
  "wine_project",
  "esrog_sector",
  "esrog_tree",
  "esrog_planting_request",
] as const;
export type DocumentOwnerType = (typeof DOCUMENT_OWNER_TYPES)[number];

// ---- Entities that can carry comments / audit / custom fields ----
export const ENTITY_TYPES = [
  "company",
  "facility",
  "plant",
  "master_ingredient",
  "company_ingredient",
  "product",
  "production",
  "inspection",
  "event",
  "loc",
  "invoice",
  "payment",
  "issue",
  "listing",
  "onboarding_task",
  "person",
  "user",
  "role",
  "reference_value",
  "setting",
  "workflow_state",
  "field_visibility",
  "custom_field_definition",
  "document",
  "comment",
  "contact",
  "ingredient_submission",
  "invite",
  "location_authorization",
  "import_job",
  "schedule_window",
  "caterer",
  "purchase_order",
  "mashgiach_invoice",
  "seal_roll",
  "seal_event",
  "seal_print_batch",
  "seal_programme",
  "fee",
  "invoice_appointment",
  "payment_method",
  "qbo_sync_job",
  "notification",
  "report",
  "job",
  "integration",
  "practitioner_approval",
  "stam_submission",
  "stam_item",
  "directory_entry",
  "directory_edition",
  "wine_project",
  "location_equipment",
  "farmer",
  "esrog_season",
  "esrog_sector",
  "esrog_tree",
  "esrog_planting_request",
  "esrog_harvest",
  "esrog_allocation",
] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

export const THEMES = ["light", "navy", "dark", "warm"] as const;
export type Theme = (typeof THEMES)[number];
